import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SiteProofLogo from '../../components/SiteProofLogo';

const Section = ({ number, title, children }) => (
  <section className="mb-8">
    <h2 className="text-lg font-semibold text-slate-100 mb-3">
      {number}. {title}
    </h2>
    <div className="space-y-3 text-sm text-slate-400 leading-relaxed">{children}</div>
  </section>
);

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-300">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3 mb-6">
            <Link to="/" className="no-underline">
              <SiteProofLogo size="sm" />
            </Link>
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to SiteProof
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mt-2">
            Cookie Policy
          </h1>
          <p className="text-sm text-slate-500 mt-2">Last updated: 28 March 2026</p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Intro box */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-8">
          <p className="text-sm text-slate-400">
            This Cookie Policy explains how <strong className="text-slate-200">SiteProof Ltd</strong> (Company
            number: <strong className="text-slate-200">17085500</strong>, registered in England and Wales) uses
            cookies and similar technologies when you use the SiteProof platform at{' '}
            <strong className="text-slate-200">site-proof.app</strong>.
          </p>
        </div>

        <Section number="1" title="What Are Cookies?">
          <p>
            Cookies are small text files that are stored on your device (computer, tablet, or phone) when you
            visit a website or use a web application. They allow the application to remember information about
            your visit, such as your login session or preferences.
          </p>
          <p>
            Cookies are widely used to make websites and applications work properly, to improve performance,
            and to provide information to the owners of the site.
          </p>
        </Section>

        <Section number="2" title="Cookies We Use">
          <p>SiteProof uses the following types of cookies:</p>

          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 mt-2">
            <h3 className="text-sm font-semibold text-slate-200 mb-2">Essential Cookies</h3>
            <p className="text-sm text-slate-400 mb-3">
              These cookies are strictly necessary for the SiteProof platform to function. Without them, you
              would not be able to log in, navigate between pages, or use core features of the platform.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-2 pr-4 font-medium text-slate-300">Cookie</th>
                    <th className="text-left py-2 pr-4 font-medium text-slate-300">Purpose</th>
                    <th className="text-left py-2 font-medium text-slate-300">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  <tr>
                    <td className="py-2 pr-4 text-slate-400">sb-auth-token</td>
                    <td className="py-2 pr-4 text-slate-400">Maintains your authenticated session so you stay logged in</td>
                    <td className="py-2 text-slate-400">Session / up to 7 days</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 text-slate-400">sb-refresh-token</td>
                    <td className="py-2 pr-4 text-slate-400">Refreshes your session token to keep you logged in without re-entering your password</td>
                    <td className="py-2 text-slate-400">Up to 30 days</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Essential cookies cannot be disabled. They are required for the platform to operate.
            </p>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 mt-4">
            <h3 className="text-sm font-semibold text-slate-200 mb-2">Analytics Cookies</h3>
            <p className="text-sm text-slate-400 mb-3">
              We may use analytics tools to help us understand how the platform is being used, which features
              are most popular, and where users encounter difficulties. This helps us improve the product.
            </p>
            <p className="text-sm text-slate-400">
              Any analytics data collected is anonymised and aggregated. We do not use analytics cookies to
              identify individual users or track you across other websites.
            </p>
            <p className="text-xs text-slate-500 mt-3">
              If analytics cookies are in use, they will be listed here. At the time of writing, SiteProof
              does not use third-party analytics cookies beyond those required for platform operation.
            </p>
          </div>
        </Section>

        <Section number="3" title="Local Storage">
          <p>
            In addition to cookies, SiteProof uses browser local storage to save certain preferences and
            application state on your device. This includes:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Your selected language preference</li>
            <li>UI state such as sidebar or panel preferences</li>
            <li>Temporary form data to prevent loss on page refresh</li>
          </ul>
          <p>
            Local storage data is stored on your device only and is not transmitted to our servers. You can
            clear local storage at any time through your browser settings.
          </p>
        </Section>

        <Section number="4" title="Third-Party Cookies">
          <p>
            Some features of SiteProof involve third-party services that may set their own cookies. These
            include:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>
              <strong className="text-slate-300">Stripe</strong> — our payment processor may set cookies when
              you access billing or subscription pages. These are governed by{' '}
              <a
                href="https://stripe.com/cookies-policy/legal"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-400 hover:text-emerald-300"
              >
                Stripe's Cookie Policy
              </a>.
            </li>
          </ul>
          <p>
            SiteProof Ltd does not control third-party cookies. Please refer to the relevant third party's
            cookie policy for more information.
          </p>
        </Section>

        <Section number="5" title="Managing Cookies">
          <p>
            You can control and manage cookies through your browser settings. Most browsers allow you to:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>View what cookies are stored on your device</li>
            <li>Delete individual cookies or all cookies</li>
            <li>Block cookies from specific websites</li>
            <li>Block all third-party cookies</li>
          </ul>
          <p>
            Please note that disabling essential cookies will prevent you from logging in to SiteProof and
            using the platform. Non-essential cookies can be disabled without affecting core functionality.
          </p>
          <p>
            For guidance on managing cookies in your browser, visit{' '}
            <a
              href="https://www.aboutcookies.org"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-400 hover:text-emerald-300"
            >
              aboutcookies.org
            </a>{' '}
            or your browser's help documentation.
          </p>
        </Section>

        <Section number="6" title="Changes to This Policy">
          <p>
            SiteProof Ltd may update this Cookie Policy from time to time to reflect changes in the
            technologies we use or applicable law. We will update the date at the top of this page when
            changes are made.
          </p>
          <p>
            Continued use of SiteProof after an update to this policy constitutes acceptance of the revised
            Cookie Policy.
          </p>
        </Section>

        <Section number="7" title="Contact Us">
          <p>
            If you have any questions about our use of cookies, please contact SiteProof Ltd at:
          </p>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 mt-3">
            <p className="font-medium text-slate-200">SiteProof Ltd</p>
            <p>Registered in England and Wales</p>
            <p>Company number: 17085500</p>
            <p>
              Email:{' '}
              <a href="mailto:support@site-proof.app" className="text-emerald-400 hover:text-emerald-300">
                support@site-proof.app
              </a>
            </p>
          </div>
        </Section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 py-8 mt-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <SiteProofLogo size="sm" />
            <div className="flex items-center gap-4 flex-wrap justify-center">
              <Link to="/terms-of-service" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
                Terms of Service
              </Link>
              <Link to="/privacy-policy" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/data-processing" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
                Data Processing Agreement
              </Link>
              <Link to="/" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
                Back to SiteProof
              </Link>
            </div>
            <p className="text-xs text-slate-600">&copy; 2026 SiteProof Ltd. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CookiePolicy;
